import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BookOpen, Calendar, Clock, Loader2, Search, X } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import { breadcrumbListSchema } from "@/lib/jsonld";
import { SITE_URL, SITE_NAME } from "@/lib/seo-constants";
import { TRACKS } from "@/lib/tracks";
import { cn } from "@/lib/utils";
import { CONTENT_API_ORIGIN } from "@/lib/api-base";
import { readSnapshot, rememberForSnapshot } from "@/lib/prerender-data";

// Only what the index cards render — the API returns full post bodies, which
// would bloat the inlined snapshot to ~400 KB.
const CARD_FIELDS = ["id", "slug", "title", "excerpt", "cover_image", "cover_image_alt", "category", "published_at", "reading_time_min", "track", "tags"];
const toCard = (p) => Object.fromEntries(CARD_FIELDS.filter((k) => k in p).map((k) => [k, p[k]]));
import ResponsiveImg from "@/components/ResponsiveImg";

const API_URL = `${CONTENT_API_ORIGIN}/api/blog`;

// Per-track index copy.
const BLOG_META = {
    residential: {
        title: "Blog — Reformity Inc. GTA Contractors",
        description:
            "Honest GTA contracting guides — fencing, decking, interlocking, landscaping and renovation pricing, bylaws, and how-to from Reformity Inc.",
        eyebrow: "Field Notes & Pricing Guides",
        heading: "The Reformity Blog.",
        sub: "Real GTA pricing, bylaws explained in plain English, and the same advice we give clients standing on their driveway with a tape measure.",
    },
    commercial: {
        title: "Commercial Blog — Reformity Inc. GTA Property Contracting",
        description:
            "Guides for property owners, developers and managers — COIs, asphalt lifecycle, accessibility, curb appeal, and managing exterior work across a portfolio.",
        eyebrow: "For Owners, Developers & Property Managers",
        heading: "Commercial Field Notes.",
        sub: "Practical guidance on protecting property value, staying compliant, and managing exterior work across a portfolio — without the runaround.",
    },
    industrial: {
        title: "Industrial Blog — Reformity Inc. GTA Site & Facility Work",
        description:
            "Guides for facility and asset managers — truck-rated paving, loading dock concrete, security fencing, traffic safety, and minimizing site downtime.",
        eyebrow: "For Facility & Asset Managers",
        heading: "Industrial Field Notes.",
        sub: "How to specify, schedule, and document heavy-duty site and facility work that holds up to industrial loads and keeps operations moving.",
    },
};

// Residential sub-category chips only.
const CATEGORY_ORDER = [
    "Fencing",
    "Decking",
    "Interlocking",
    "Concrete",
    "Landscaping",
    "Renovations",
    "General",
];

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return null;
    }
};

const blogItemListSchema = (posts, blogBase) => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Reformity Inc. Blog",
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: posts.length,
    itemListElement: posts.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}${blogBase}/${p.slug}`,
        name: p.title,
    })),
});

const PostCard = ({ post, blogBase }) => {
    const dateLabel = formatDate(post.published_at);
    return (
        <Link
            to={`${blogBase}/${post.slug}`}
            className="group block bg-white border border-brand-dark/10 hover:border-brand-cyan transition-colors duration-200 flex flex-col h-full"
            data-testid={`blog-card-${post.slug}`}
        >
            {post.cover_image ? (
                <div className="aspect-[16/10] overflow-hidden bg-brand-offwhite">
                    <ResponsiveImg
                        src={post.cover_image}
                        alt={post.cover_image_alt || post.title}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                    />
                </div>
            ) : (
                <div className="aspect-[16/10] bg-brand-offwhite flex items-center justify-center">
                    <BookOpen className="h-10 w-10 text-brand-gray" strokeWidth={1.5} />
                </div>
            )}
            <div className="flex flex-1 flex-col p-6 md:p-7">
                <div className="flex items-center gap-3 mb-3 text-[10px] uppercase tracking-wider font-semibold">
                    <span
                        className="bg-brand-coral text-white px-2 py-1"
                        data-testid={`blog-card-category-${post.slug}`}
                    >
                        {post.category}
                    </span>
                    {dateLabel && (
                        <span className="flex items-center gap-1 text-brand-gray">
                            <Calendar className="h-3 w-3" strokeWidth={2.25} />
                            {dateLabel}
                        </span>
                    )}
                </div>
                <h2 className="font-display text-2xl md:text-3xl tracking-wide text-brand-dark leading-tight group-hover:text-brand-cyan transition-colors mb-3">
                    {post.title}
                </h2>
                <p className="text-sm md:text-[15px] text-brand-dark/75 leading-relaxed flex-1 mb-5">
                    {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-brand-dark/10">
                    <span className="flex items-center gap-1.5 text-xs text-brand-gray">
                        <Clock className="h-3.5 w-3.5" strokeWidth={2.25} />
                        {post.reading_time_min} min read
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-coral group-hover:text-brand-cyan transition-colors">
                        Read article
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2.5} />
                    </span>
                </div>
            </div>
        </Link>
    );
};

const Blog = ({ trackKey = "residential" }) => {
    const track = TRACKS[trackKey];
    const meta = BLOG_META[trackKey] || BLOG_META.residential;
    const showCategoryFilter = trackKey === "residential";

    const snapKey = `blog-index:${trackKey}`;
    const [posts, setPosts] = useState(() => readSnapshot(snapKey) || []);
    const [loading, setLoading] = useState(() => !readSnapshot(snapKey));
    const [error, setError] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();

    const categoryParam = (searchParams.get("category") || "").trim();
    const activeCategory =
        CATEGORY_ORDER.find((c) => c.toLowerCase() === categoryParam.toLowerCase()) || "all";
    const queryParam = (searchParams.get("q") || "").trim();

    useEffect(() => {
        if (readSnapshot(snapKey)) return undefined; // hydrated from the prerender snapshot
        let active = true;
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await axios.get(API_URL, { params: { track: trackKey } });
                if (!active) return;
                const list = (Array.isArray(res.data) ? res.data : []).map(toCard);
                rememberForSnapshot(snapKey, list);
                setPosts(list);
            } catch (err) {
                if (!active) return;
                const detail = err?.response?.data?.detail;
                setError(
                    typeof detail === "string"
                        ? detail
                        : "Could not load blog posts. Try refreshing.",
                );
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackKey]);

    const handleFilter = (cat) => {
        const next = new URLSearchParams(searchParams);
        if (cat === "all") next.delete("category");
        else next.set("category", cat.toLowerCase());
        setSearchParams(next, { replace: true });
    };

    const handleSearch = (value) => {
        const next = new URLSearchParams(searchParams);
        if (value.trim()) next.set("q", value);
        else next.delete("q");
        setSearchParams(next, { replace: true });
    };

    const byCategory =
        !showCategoryFilter || activeCategory === "all"
            ? posts
            : posts.filter(
                  (p) => (p.category || "").toLowerCase() === activeCategory.toLowerCase(),
              );
    const filtered = queryParam
        ? byCategory.filter((p) =>
              `${p.title} ${p.excerpt} ${(p.tags || []).join(" ")}`
                  .toLowerCase()
                  .includes(queryParam.toLowerCase()),
          )
        : byCategory;

    const countFor = (cat) =>
        cat === "all"
            ? posts.length
            : posts.filter((p) => (p.category || "").toLowerCase() === cat.toLowerCase()).length;

    return (
        <>
            <Seo
                title={meta.title}
                description={meta.description}
                path={track.blog}
                jsonLd={[
                    breadcrumbListSchema([
                        ...(trackKey === "residential"
                            ? []
                            : [{ name: track.label, path: track.home }]),
                        { name: "Blog", path: track.blog },
                    ]),
                    blogItemListSchema(posts, track.blog),
                ]}
            />

            <Section dark className="py-14 md:py-16" data-testid="blog-hero">
                <Container>
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                        {meta.eyebrow}
                    </p>
                    <h1
                        className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                        data-testid="blog-title"
                    >
                        {meta.heading}
                    </h1>
                    <p className="mt-5 max-w-2xl text-base md:text-lg text-white/75 font-light italic leading-relaxed">
                        {meta.sub}
                    </p>
                </Container>
            </Section>

            {showCategoryFilter && (
                <Section className="py-6 md:py-8" bg="white" data-testid="blog-filters">
                    <Container>
                        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter posts by category">
                            {[{ key: "all", label: "All Posts" }, ...CATEGORY_ORDER.map((c) => ({ key: c, label: c }))].map(
                                (f) => {
                                    const active = activeCategory.toLowerCase() === f.key.toLowerCase();
                                    const count = countFor(f.key);
                                    return (
                                        <button
                                            key={f.key}
                                            type="button"
                                            onClick={() => handleFilter(f.key)}
                                            role="tab"
                                            aria-selected={active}
                                            className={cn(
                                                "inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors duration-200 border",
                                                active
                                                    ? "bg-brand-dark text-white border-brand-dark"
                                                    : "bg-white text-brand-dark border-brand-dark/15 hover:border-brand-cyan hover:text-brand-cyan",
                                            )}
                                            data-testid={`blog-filter-${f.key.toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            {f.label}
                                            <span
                                                className={cn(
                                                    "text-[10px]",
                                                    active ? "text-white/70" : "text-brand-gray",
                                                )}
                                            >
                                                ({count})
                                            </span>
                                        </button>
                                    );
                                },
                            )}
                        </div>
                    </Container>
                </Section>
            )}

            <Section className="py-12 md:py-20" bg="white" data-testid="blog-grid-section">
                <Container>
                    {/* Search */}
                    <div className="mb-8 max-w-md">
                        <div className="relative">
                            <Search
                                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray"
                                strokeWidth={2.25}
                                aria-hidden="true"
                            />
                            <input
                                type="search"
                                value={queryParam}
                                onChange={(e) => handleSearch(e.target.value)}
                                placeholder="Search articles…"
                                aria-label="Search articles"
                                className="block w-full bg-white border border-brand-dark/15 pl-9 pr-9 py-2.5 text-sm text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none transition-colors"
                                data-testid="blog-search"
                            />
                            {queryParam && (
                                <button
                                    type="button"
                                    onClick={() => handleSearch("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-brand-coral transition-colors"
                                    aria-label="Clear search"
                                    data-testid="blog-search-clear"
                                >
                                    <X className="h-4 w-4" strokeWidth={2.5} />
                                </button>
                            )}
                        </div>
                        {queryParam && (
                            <p className="mt-2 text-xs text-brand-gray">
                                {filtered.length} result{filtered.length === 1 ? "" : "s"} for
                                &ldquo;{queryParam}&rdquo;
                            </p>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-20" data-testid="blog-loading">
                            <Loader2 className="h-8 w-8 text-brand-cyan animate-spin" strokeWidth={2} />
                        </div>
                    ) : error ? (
                        <div className="border-l-4 border-brand-coral bg-brand-coral/10 p-5 text-sm text-brand-dark" data-testid="blog-error">
                            {error}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="bg-brand-offwhite border-2 border-dashed border-brand-dark/15 p-16 text-center" data-testid="blog-empty">
                            <BookOpen className="h-10 w-10 mx-auto text-brand-gray mb-4" strokeWidth={1.5} />
                            <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-2">
                                No posts in this category yet.
                            </h3>
                            <p className="text-sm text-brand-dark/60">
                                Check back soon — new articles get added every month.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="blog-grid">
                            {filtered.map((p) => (
                                <PostCard key={p.id || p.slug} post={p} blogBase={track.blog} />
                            ))}
                        </div>
                    )}
                </Container>
            </Section>

            <Section dark className="py-16 md:py-20" data-testid="blog-cta-band">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                                Have a project in mind?
                            </p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-white leading-none">
                                Get a free, written quote.
                            </h2>
                            <p className="mt-5 text-base text-white/70 max-w-xl">
                                We measure, listen, and send a fixed-price scope — across the
                                GTA, from {SITE_NAME}.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                            <BrandButton asChild variant="primary" size="lg" className="w-full lg:w-auto">
                                <Link to={track.contact} data-testid="blog-cta-quote">
                                    Start My Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
};

export default Blog;
